/**
 * Navbar HORIZONTALE sticky avec FOND COLORÉ (primary teal).
 * Items + dropdowns + actions à droite en texte primary-foreground.
 *
 * Présentation E (module `ui_template_e`, ADR 0068) : chrome bleu nuit et
 * filet dégradé (index.css, `#app-navbar`), entrée active soulignée en cyan,
 * menus ouverts au CLIC (Échap et clic extérieur ferment, via Radix). Mêmes
 * menus, mêmes entrées, mêmes droits.
 */
import { ChevronDown } from "lucide-react";
import { Fragment, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { APP_LOGO_TEXT, activeNavHref, landingPathFor, visibleNavGroups } from "@/config/navigation";
import { useAuthStore } from "@/features/auth/store";
import { HelpMenu } from "@/features/help/components/HelpMenu";
import { NotificationsBell } from "@/features/notifications/components/NotificationsBell";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AmountsVisibilityToggle } from "./AmountsVisibilityToggle";
import { ClinicSwitcher } from "./ClinicSwitcher";
import { GlobalSearch } from "./GlobalSearch";
import { MobileNav } from "./MobileNav";
import { ThemeToggle } from "./ThemeToggle";
import { UserMenu } from "./UserMenu";
import { cn } from "@/lib/utils";
import { contentMaxWidthClass } from "@/lib/content-width";
import { useIsUiTemplateE } from "@/lib/hooks/use-ui-template";

/** Entrées de la barre sous la présentation E : pleine hauteur, filet bas. */
const E_ITEM = "self-stretch inline-flex items-center border-y-[3px] border-transparent";
const E_ITEM_IDLE =
  "text-chrome-muted hover:bg-chrome-line hover:text-chrome-foreground data-[state=open]:bg-chrome-line data-[state=open]:text-chrome-foreground";
const E_ITEM_ACTIVE = "border-b-brand-cyan font-semibold text-chrome-foreground";

export function TopNavbar() {
  const me = useAuthStore((s) => s.me);
  const { pathname, search } = useLocation();
  const uiE = useIsUiTemplateE();

  // Ouverture des menus AU SURVOL (délai de fermeture pour traverser l'écart
  // trigger → contenu) ; le clic reste fonctionnel (clavier, tactile).
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const closeTimer = useRef<number | null>(null);
  const hoverOpen = (label: string) => {
    // Présentation E : ouverture au clic seulement (le survol ne fait rien).
    if (uiE) return;
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };
  const hoverClose = () => {
    if (uiE) return;
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenMenu(null), 180);
  };

  // Menus visibles (console, menus cliniques ou nouvelle organisation, ADR
  // 0061) et entrée active : règles pures de `config/navigation`.
  const visibleGroups = visibleNavGroups(me);
  const activeHref = activeNavHref(visibleGroups, pathname, search);

  return (
    <header
      id="app-navbar"
      className="sticky top-0 z-40 w-full border-b border-primary bg-primary text-primary-foreground shadow-sm"
    >
      <div
        className={cn(
          "mx-auto flex h-16 w-full items-center gap-2 px-3 sm:gap-4 sm:px-4 lg:px-6",
          contentMaxWidthClass(me),
        )}
      >
        {/* Mobile hamburger */}
        <div className="shrink-0 lg:hidden">
          <MobileNav />
        </div>

        {/* Logo */}
        <Link
          to={landingPathFor(me)}
          className={cn(
            "flex shrink-0 items-center gap-2 text-lg font-semibold text-primary-foreground hover:opacity-90",
            uiE && "font-title text-[22px] font-bold tracking-[0.08em]",
          )}
        >
          {APP_LOGO_TEXT}
        </Link>

        {/* Desktop navigation */}
        <nav
          className={cn("hidden flex-1 items-center lg:flex", uiE ? "gap-0 self-stretch" : "gap-1")}
          data-help="nav.menu"
        >
          {visibleGroups.map((group) => {
            if (group.items.length === 1) {
              const item = group.items[0]!;
              const active = item.href === activeHref;
              // Paramètres en engrenage (menus simplifiés, ADR 0069).
              const Icon = item.iconOnly ? item.icon : undefined;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  aria-label={Icon ? item.label : undefined}
                  title={Icon ? item.label : undefined}
                  aria-current={uiE && active ? "page" : undefined}
                  className={cn(
                    "whitespace-nowrap px-2 py-2 text-sm font-medium transition-colors xl:px-3",
                    uiE
                      ? cn(E_ITEM, active ? E_ITEM_ACTIVE : E_ITEM_IDLE)
                      : active
                        ? "border-b-2 border-primary-foreground text-primary-foreground"
                        : "text-primary-foreground/80 hover:text-primary-foreground",
                  )}
                >
                  {Icon ? <Icon className="h-4 w-4" aria-hidden /> : item.label}
                </Link>
              );
            }
            const isActiveGroup = group.items.some((i) => i.href === activeHref);
            return (
              <DropdownMenu
                key={group.label}
                open={openMenu === group.label}
                onOpenChange={(o) => setOpenMenu(o ? group.label : null)}
                modal={false}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    onMouseEnter={() => hoverOpen(group.label)}
                    onMouseLeave={hoverClose}
                    className={cn(
                      "inline-flex items-center gap-1 whitespace-nowrap px-2 py-2 text-sm font-medium transition-colors xl:px-3",
                      "hover:text-primary-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/60",
                      uiE
                        ? cn(E_ITEM, isActiveGroup ? E_ITEM_ACTIVE : E_ITEM_IDLE, "focus-visible:ring-brand-cyan")
                        : isActiveGroup
                          ? "border-b-2 border-primary-foreground text-primary-foreground"
                          : "text-primary-foreground/80",
                    )}
                  >
                    {group.label}
                    {/* La rubrique de la page en cours ne se lit pas qu'à
                        la couleur (filet cyan, gras) : annoncée aussi. */}
                    {uiE && isActiveGroup && <span className="sr-only"> (rubrique en cours)</span>}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className={cn(
                    "max-h-[calc(100vh-5rem)] overflow-y-auto",
                    // Lignes d'aide sous les entrées (ADR 0069) : menu plus large.
                    group.items.some((i) => i.hint) ? "w-72" : "w-60",
                    uiE && "border-t-[3px] border-t-primary-text",
                  )}
                  onMouseEnter={() => hoverOpen(group.label)}
                  onMouseLeave={hoverClose}
                >
                  {group.sections.map((section, index) => (
                    <Fragment key={section.label ?? `top-${index}`}>
                      {/* Sous-titres (ADR 0061) : seulement s'ils portent une entrée visible. */}
                      {section.label && index > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuGroup aria-label={section.label}>
                        {section.label && <DropdownMenuLabel>{section.label}</DropdownMenuLabel>}
                        {section.items.map((item) => {
                          const Icon = item.icon;
                          const isActiveItem = item.href === activeHref;
                          return (
                            <DropdownMenuItem key={item.href} asChild>
                              <Link
                                to={item.href}
                                aria-current={isActiveItem ? "page" : undefined}
                                className={cn(
                                  "flex items-center gap-2 cursor-pointer",
                                  isActiveItem && "bg-primary/10 font-semibold text-primary-text",
                                )}
                              >
                                {Icon && <Icon className={cn("h-4 w-4 shrink-0", item.hint && "self-start mt-0.5")} />}
                                {item.hint ? (
                                  <span className="flex min-w-0 flex-col">
                                    <span>{item.label}</span>
                                    <span className="text-xs font-normal text-muted-foreground">{item.hint}</span>
                                  </span>
                                ) : (
                                  item.label
                                )}
                                {isActiveItem && (
                                  <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary-text" aria-label="actif" />
                                )}
                              </Link>
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuGroup>
                    </Fragment>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })}
        </nav>

        {/* Right side */}
        <div className="ml-auto flex min-w-0 items-center gap-1 xl:gap-2">
          {/* Icônes secondaires (recherche, thème, montants, aide) : repliées
              dans le drawer sous sm, sinon la barre déborde à 320-375 px. */}
          <div className="hidden sm:flex" data-help="nav.search">
            <GlobalSearch />
          </div>
          <div className="hidden sm:flex">
            <ThemeToggle />
          </div>
          <div data-help="nav.amounts" className="hidden sm:flex">
            <AmountsVisibilityToggle />
          </div>
          <NotificationsBell />
          {/* Sélecteur unique de clinique = bascule entre les 3 organisations. */}
          <div data-help="nav.clinic" className="flex min-w-0">
            <ClinicSwitcher />
          </div>
          <div className="hidden sm:flex">
            <HelpMenu />
          </div>
          <div data-help="nav.user" className="flex shrink-0">
            <UserMenu />
          </div>
        </div>
      </div>
    </header>
  );
}
